import PropTypes from 'prop-types';
import { formatCurrency } from '../../utils/formatters';
import './OrderItemsTable.css';

const OrderItemsTable = ({
  items = [],
  editable = false,
  isEmployee = false,
  onUpdateQuantity,
  onUpdatePrice,
  onUpdateShipping,
  onRemoveItem,
  shippingChargeDollars = 0,
  discountCents = 0,
  creditBalanceCents = 0,
}) => {
  const handleDecrement = (item) => {
    const qty = parseInt(item.quantity, 10) || 1;
    if (qty <= 1) {
      if (onRemoveItem) onRemoveItem(item.id);
    } else {
      if (onUpdateQuantity) onUpdateQuantity(item.id, qty - 1);
    }
  };

  const handleIncrement = (item) => {
    const qty = parseInt(item.quantity, 10) || 0;
    if (onUpdateQuantity) onUpdateQuantity(item.id, qty + 1);
  };

  // Safely resolve a price/subtotal that may legitimately be 0 (samples)
  const resolvePrice = (...candidates) => {
    for (const v of candidates) {
      if (v != null && v !== '') return parseFloat(v);
    }
    return 0;
  };

  const calculateTotal = () => {
    const itemsTotal = items.reduce((sum, item) => {
      if (item.is_sample) return sum;
      const unitPrice = resolvePrice(item.unit_price_dollars, item.unit_price, item.product_variant?.price_dollars, item.variant?.price_dollars, item.product_variant?.price, item.variant?.price);
      const quantity = parseInt(item.quantity, 10) || 0;
      const subtotal = resolvePrice(item.subtotal_dollars, item.subtotal) || unitPrice * quantity;
      return sum + subtotal;
    }, 0);
    return itemsTotal + (parseFloat(shippingChargeDollars) || 0) - (discountCents / 100);
  };

  if (items.length === 0) {
    return (
      <div className="order-items order-items--empty">
        <p>No items in this order.</p>
      </div>
    );
  }

  return (
    <div className="order-items">
      <div className="order-items__list">
        {items.map((item) => {
          const isCustom = item.isCustom || (!item.product_variant && !item.product && item.description);
          const productName = isCustom
            ? (item.description || 'Custom Item')
            : (item.product_variant?.product_name || item.product?.name || 'Unknown Product');
          const variantName = isCustom ? '' : (item.product_variant?.variant_name || item.variant?.variant_name || '');
          const unitPrice = resolvePrice(item.unit_price_dollars, item.unit_price, item.product_variant?.price_dollars, item.variant?.price_dollars, item.product_variant?.price, item.variant?.price);
          const quantity = parseInt(item.quantity, 10) || 0;
          const subtotal = item.is_sample ? 0 : (resolvePrice(item.subtotal_dollars, item.subtotal) || unitPrice * quantity);

          const isSample = item.is_sample || false;

          return (
            <div key={item.id} className={`order-items__item${isSample ? ' order-items__item--sample' : ''}`}>
              <div className="order-items__item-info">
                <span className="order-items__item-name">
                  {productName}
                  {isSample && <span className="order-items__sample-badge">Sample</span>}
                </span>
                {variantName && (
                  <span className="order-items__item-variant">{variantName}</span>
                )}
              </div>

              {editable ? (
                isSample ? (
                  <div className="order-items__sample-qty">{quantity}</div>
                ) : (
                  <div className="order-items__edit-controls">
                    <div className="order-items__stepper">
                      <button
                        type="button"
                        className="order-items__stepper-btn"
                        onClick={() => handleDecrement(item)}
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className="order-items__stepper-qty">{quantity}</span>
                      <button
                        type="button"
                        className="order-items__stepper-btn"
                        onClick={() => handleIncrement(item)}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                    {isEmployee && onUpdatePrice && (
                      <div className="order-items__price-override">
                        <span className="order-items__price-prefix">$</span>
                        <input
                          type="number"
                          className="order-items__price-input"
                          min="0"
                          step="0.01"
                          value={item.unit_price ?? unitPrice}
                          onChange={(e) => onUpdatePrice(item.id, e.target.value)}
                          aria-label="Unit price override"
                        />
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div className="order-items__qty-label">
                  {quantity} × {isSample ? 'complimentary' : formatCurrency(unitPrice)}
                </div>
              )}

              <div className="order-items__item-subtotal">
                {isSample ? <span className="order-items__sample-free">Free</span> : formatCurrency(subtotal)}
              </div>

              {editable && (
                <button
                  type="button"
                  className="order-items__item-remove"
                  onClick={() => onRemoveItem && onRemoveItem(item.id)}
                  aria-label="Remove item"
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="order-items__shipping">
        <span className="order-items__shipping-label">Shipping</span>
        {isEmployee && onUpdateShipping ? (
          <div className="order-items__price-override">
            <span className="order-items__price-prefix">$</span>
            <input
              type="number"
              className="order-items__price-input"
              min="0"
              step="0.01"
              value={shippingChargeDollars}
              onChange={(e) => onUpdateShipping(e.target.value)}
              aria-label="Shipping charge override"
            />
          </div>
        ) : (
          <span className="order-items__shipping-amount">{formatCurrency(parseFloat(shippingChargeDollars) || 0)}</span>
        )}
      </div>
      {discountCents > 0 && (
        <div className="order-items__discount">
          <span className="order-items__discount-label">Discount</span>
          <span className="order-items__discount-amount">−{formatCurrency(discountCents / 100)}</span>
        </div>
      )}
      <div className="order-items__total">
        <span className="order-items__total-label">Order Total</span>
        <span className="order-items__total-amount">{formatCurrency(calculateTotal())}</span>
      </div>
      {creditBalanceCents > 0 && (
        <div className="order-items__credit-note">
          You have <strong>{formatCurrency(creditBalanceCents / 100)}</strong> in account credit — it will be applied to your invoice.
        </div>
      )}
    </div>
  );
};

OrderItemsTable.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      product_variant: PropTypes.object,
      product: PropTypes.object,
      variant: PropTypes.object,
      quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      unit_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      subtotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  editable: PropTypes.bool,
  isEmployee: PropTypes.bool,
  onUpdateQuantity: PropTypes.func,
  onUpdatePrice: PropTypes.func,
  onUpdateShipping: PropTypes.func,
  onRemoveItem: PropTypes.func,
  shippingChargeDollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  discountCents: PropTypes.number,
  creditBalanceCents: PropTypes.number,
};

export default OrderItemsTable;
